import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { NftDataType, CollectionType } from "../../../lib/types";
import NftBurnCard from "../../shop/NftBurnCard";

interface SelectBurnNFTDialogProps {
    dialogOpen: boolean;
    selectedNft: NftDataType | undefined;
    nftLoading: boolean;
    AllFilteredNFTs: NftDataType[];
    selectedCollection: CollectionType;
    selectNftHandle: (nft: NftDataType) => void;
    handleDialogOpen: (open: boolean) => void;
    handleClose: () => void;
}

const SelectBurnNFTDialog = ({
    dialogOpen,
    selectedNft,
    nftLoading,
    AllFilteredNFTs,
    selectedCollection,
    selectNftHandle,
    handleDialogOpen,
    handleClose,
}: SelectBurnNFTDialogProps) => {
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (dialogOpen) {
            setSearchTerm("");
        }
    }, [dialogOpen]);

    const handleOpenChange = (open: boolean) => {
        handleDialogOpen(open);
        if (!open) {
            handleClose();
        }
    };

    const filteredNFTs = AllFilteredNFTs.filter((nft) =>
        nft.nftname?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    );

    return (
        <AnimatePresence>
            {dialogOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => handleOpenChange(false)}
                    />

                    {/* Modal Content */}
                    <motion.div
                        className="fixed top-1/2 left-1/2 w-[90vw] max-w-5xl h-[80vh] bg-[#111827] rounded-xl border border-orange-500/20 shadow-2xl z-50 -translate-x-1/2 -translate-y-1/2 flex flex-col overflow-hidden"
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b border-orange-500/10 bg-gradient-to-r from-orange-500/5 to-transparent">
                            <h2 className="text-xl font-light tracking-wide bg-gradient-to-r from-orange-400 to-orange-200 bg-clip-text text-transparent font-['Space_Grotesk']">
                                Select NFT to Burn
                            </h2>
                            <button
                                onClick={() => handleOpenChange(false)}
                                className="text-orange-300/70 hover:text-orange-200 hover:bg-orange-500/10 p-2 rounded-full transition-colors"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-hidden flex flex-col p-4">
                            {/* Search Bar */}
                            <div className="mb-4 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-orange-300/70" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 border border-orange-500/20 rounded-lg leading-5 bg-gray-900/80 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 sm:text-sm transition-colors"
                                    placeholder="Search your NFTs..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* NFT Grid */}
                            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                                {nftLoading ? (
                                    <div className="h-full flex flex-col items-center justify-center gap-4">
                                        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                                        <p className="text-orange-300/70 text-sm font-['Space_Grotesk']">Loading your NFTs...</p>
                                    </div>
                                ) : filteredNFTs.length > 0 ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 pb-4">
                                        <AnimatePresence>
                                            {filteredNFTs.map((nft, index) => (
                                                <motion.div
                                                    key={nft.mint}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, scale: 0.9 }}
                                                    transition={{ duration: 0.2, delay: index * 0.02 }}
                                                    onClick={() => selectNftHandle(nft)}
                                                    className={`relative cursor-pointer rounded-xl overflow-hidden transition-all duration-200 group ${selectedNft?.mint === nft.mint
                                                            ? 'ring-2 ring-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.3)]'
                                                            : 'hover:ring-1 hover:ring-orange-500/40 hover:-translate-y-1'
                                                        }`}
                                                >
                                                    <div className="transform scale-95 group-hover:scale-100 transition-transform duration-200">
                                                        <NftBurnCard
                                                            nft={nft}
                                                            onClick={() => { }}
                                                            selected={selectedNft?.mint === nft.mint}
                                                        />
                                                    </div>

                                                    {selectedNft?.mint === nft.mint && (
                                                        <motion.div
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            className="absolute top-2 right-2 bg-gradient-to-r from-orange-500 to-orange-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg z-10"
                                                        >
                                                            Selected
                                                        </motion.div>
                                                    )}
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center border border-dashed border-orange-500/20 rounded-xl bg-gray-900/30">
                                        <p className="text-orange-300/70 font-medium">No NFTs found</p>
                                        <p className="text-gray-500 text-sm mt-1">Try adjusting your search</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-orange-500/10 bg-gray-900/50 flex justify-between items-center">
                            <p className="text-gray-400 text-xs font-['Space_Grotesk']">
                                {filteredNFTs.length} {filteredNFTs.length === 1 ? 'NFT' : 'NFTs'} available
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleOpenChange(false)}
                                    className="px-4 py-2 rounded-lg border border-orange-500/30 text-orange-300 text-xs font-medium hover:bg-orange-500/10 hover:border-orange-500/50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleClose}
                                    disabled={!selectedNft}
                                    className={`px-4 py-2 rounded-lg text-xs font-bold text-white shadow-lg transition-all ${selectedNft
                                            ? 'bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-400 hover:to-orange-300 shadow-orange-500/20'
                                            : 'bg-gray-700 cursor-not-allowed opacity-50'
                                        }`}
                                >
                                    {selectedNft ? 'Confirm Burn' : 'Select NFT'}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SelectBurnNFTDialog;
